import { Box, Button, Grid, RadioGroup, Tooltip, useTheme } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { AddIcon } from "@zfp-akademie-a-s/zfp-design/icons";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { FamilyMemberDrawer } from "Components/Analyses/Family/FamilyMemberDrawer";
import { getGetAnalysisClientsQueryKey, useDeleteClient, useGetAnalysisClients } from "Infrastructure/ApiZfp/AnalyzerApi";
import { AnalysisClientItemAgeCategoryType, AnalysisClientItemRelationType } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { ChildTile } from "./ChildTile";

type Props = {
  readonly selectedClientId: string | undefined;
  readonly editMode?: boolean;
  readonly onSelect: (clientId: string) => void;
  readonly disabled?: boolean;
};

const CHILD_AGE_CATEGORIES: AnalysisClientItemAgeCategoryType[] = [
  AnalysisClientItemAgeCategoryType.child,
  AnalysisClientItemAgeCategoryType.plannedChild,
];

export const ChildrenManagement: React.FC<Props> = ({ selectedClientId, editMode, onSelect, disabled }) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { analysisId, versionId } = useParams() as {
    analysisId: string;
    versionId: string;
  };
  const { t } = useResource();
  const { canAddFamilyMember } = useContext(AnalysisDetailContext);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const { data } = useGetAnalysisClients(analysisId, versionId);

  useEffect(() => {
    if (!selectedClientId && data?.clients && data.clients.filter((c) => CHILD_AGE_CATEGORIES.includes(c.ageCategoryType)).length > 0) {
      onSelect(data.clients.filter((c) => CHILD_AGE_CATEGORIES.includes(c.ageCategoryType))[0].guid);
    }
  }, [data?.clients, onSelect, selectedClientId]);

  const { mutateAsync: deleteClient } = useDeleteClient({
    mutation: {
      onSuccess: () => {
        toast.success(t(Resources.AnalysisDetail.Toast.FamilyMemberDeleted));
        queryClient.invalidateQueries({ queryKey: getGetAnalysisClientsQueryKey(analysisId, versionId) });
      },
    },
  });

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const handleClientDelete = async (clientId: string) => {
    try {
      if (!clientId) throw new Error();

      await deleteClient({
        clientGuid: clientId,
      });

      const first = data?.clients.find((client) => client.guid !== clientId && CHILD_AGE_CATEGORIES.includes(client.ageCategoryType));

      if (first) {
        onSelect(first.guid);
      }
    } catch {
      toast.error(t(Resources.AnalysisDetail.Toast.FamilyMemberDeleteError));
    }
  };

  return (
    <>
      <Grid container spacing={Spacing.L}>
        <Grid size={12}>
          <RadioGroup
            aria-label={t(Resources.AnalysisDetail.Goals.GoalType.Children)}
            value={selectedClientId ?? null}
            onChange={(_, clientId) => onSelect(clientId)}
            sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: Spacing.L }}
          >
            {data?.clients
              .filter((client) => CHILD_AGE_CATEGORIES.includes(client.ageCategoryType))
              .map((client) => {
                const disabledEdit = client.guid !== selectedClientId && editMode;

                return (
                  <ChildTile
                    key={client.guid}
                    clientId={client.guid}
                    firstName={client.firstName}
                    lastName={client.surname}
                    birthDate={client.birthDate ? new Date(client.birthDate) : undefined}
                    onDelete={editMode ? undefined : () => handleClientDelete(client.guid)}
                    disabled={disabledEdit || disabled}
                  />
                );
              })}
          </RadioGroup>
        </Grid>
        {!disabled && (
          <Grid size={12}>
            <Tooltip title={canAddFamilyMember ? "" : t(Resources.AnalysisDetail.Family.AddMemberOwnerOnly)}>
              {/* a disabled button fires no pointer events, the tooltip listens on the wrapper */}
              <Box component="span" display="inline-flex">
                <Button disabled={!canAddFamilyMember} color="primary" variant="text" onClick={() => handleDrawerOpen()}>
                  <AddIcon htmlColor={canAddFamilyMember ? theme.palette.primary.main : theme.palette.text.disabled} />
                  &nbsp;{t(Resources.AnalysisDetail.Goals.AddExistingChild)}
                </Button>
              </Box>
            </Tooltip>
          </Grid>
        )}
      </Grid>
      <FamilyMemberDrawer clientRelationType={AnalysisClientItemRelationType.RelatedClient} open={drawerOpen} onClose={handleDrawerClose} />
    </>
  );
};
